import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ContributionAgreementScreen from './ContributionAgreementScreen';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { getInviteByToken } from '../utils/inviteToken';
import { sendOtp } from '../../auth/otp/sendOtp';
import { loginWithPin } from '../../auth/utils/pinLogin';

// Route: /invite/:token  — per-member token invite flow
// Route: /join/:groupId  — group-level self-registration (kept for group-setup invite links)

// ─── Invite Landing ───────────────────────────────────────────────────────────

const InviteLandingPage = ({ invite, onAccept }) => {
  const { members: member, groups: group } = invite;
  const positions = member?.hands?.map(h => h.position_number).sort((a, b) => a - b) || [];
  const amount = group?.contribution_amount_per_hand;
  const frequency = group?.contribution_frequency;

  const FREQ_LABELS = { weekly: 'Weekly', 'bi-weekly': 'Bi-weekly', monthly: 'Monthly' };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-neutral-100">
      <div className="mb-8 text-center">
        <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-neutral-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-neutral-900">You're Invited!</h1>
        <p className="text-neutral-600 mt-2">You've been added to a savings group on EsuSol</p>
      </div>

      <div className="max-w-sm mx-auto w-full space-y-4">
        <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-5 shadow-sm">
          <h2 className="text-base font-semibold text-neutral-900 mb-4">{group?.group_name}</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-500">Member name</span>
              <span className="font-medium text-neutral-900">{member?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Contribution</span>
              <span className="font-medium text-neutral-900">
                ${parseFloat(amount || 0).toFixed(2)} per hand
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Frequency</span>
              <span className="font-medium text-neutral-900">
                {FREQ_LABELS[frequency] || frequency}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-500">Your hand(s)</span>
              <span className="font-medium text-neutral-900">{positions.join(', ')}</span>
            </div>
            {group?.start_date && (
              <div className="flex justify-between">
                <span className="text-neutral-500">First due date</span>
                <span className="font-medium text-neutral-900">
                  {/* Parse date-only at LOCAL midnight (avoid the UTC off-by-one). */}
                  {new Date(`${group.start_date}T00:00:00`).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric'
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
          <p className="text-xs text-warning-800">
            <strong>Next step:</strong> You'll review the group contribution agreement
            and verify your email to access your personal dashboard.
          </p>
        </div>
      </div>

      <div className="mt-8 max-w-sm mx-auto w-full">
        <button
          onClick={onAccept}
          className="w-full bg-primary-600 text-neutral-50 rounded-lg px-4 py-4 text-base font-semibold hover:bg-primary-700"
        >
          Review Agreement →
        </button>
      </div>
    </div>
  );
};

// ─── Dev OTP Banner ───────────────────────────────────────────────────────────

const DevOtpBanner = ({ code, onDismiss }) => (
  <div className="fixed top-4 left-4 right-4 max-w-sm mx-auto bg-warning-100 border border-warning-500 rounded-lg p-3 flex items-start justify-between gap-3 z-50">
    <p className="text-sm text-warning-900">
      <strong>DEV MODE</strong> — your code is{' '}
      <strong className="tracking-widest">{code}</strong>
    </p>
    <button onClick={onDismiss} className="text-warning-900 text-lg leading-none" aria-label="Dismiss">✕</button>
  </div>
);

// ─── Verify Phone (OTP) Screen ────────────────────────────────────────────────

const PhoneVerifyScreen = ({ phoneMasked, codeSent, onSend, onResend, onSubmit, sending, loading, error, onBack }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);

  const handleChange = (index, value) => {
    if (value.length > 1) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) document.getElementById(`join-otp-${index + 1}`)?.focus();
  };
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) document.getElementById(`join-otp-${index - 1}`)?.focus();
  };
  const submit = (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) return;
    onSubmit(code);
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-neutral-100">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Verify Your Phone</h1>
        <p className="text-neutral-600">
          We'll send a 6-digit code to <strong>{phoneMasked || 'your phone'}</strong>.
        </p>
      </div>

      {/* Short SMS-code reminder. The full messaging consent (SMS + WhatsApp, OTP +
          ongoing notifications) is presented and agreed at the Agreement step and
          recorded as messaging_consent_version. Finalized copy (Frannie). */}
      <p className="text-sm text-neutral-600 leading-relaxed mb-6 border border-neutral-200 bg-neutral-50 rounded-lg p-3">
        We'll text your verification code by SMS. Standard message and data rates may apply. Reply STOP to opt out.
      </p>

      {!codeSent ? (
        <div className="flex-1 space-y-4">
          {error && <p className="text-sm text-error-600 text-center">{error}</p>}
          <button
            type="button"
            onClick={onSend}
            disabled={sending}
            className="w-full px-4 py-3 bg-primary-600 text-neutral-50 rounded-lg font-semibold disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send code'}
          </button>
          <button type="button" onClick={onBack} className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-neutral-700 font-medium">
            Back
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="flex-1 space-y-6">
          <div className="flex justify-center space-x-2">
            {otp.map((digit, i) => (
              <input
                key={i}
                id={`join-otp-${i}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="w-11 h-12 text-center text-xl font-bold border-2 border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoComplete="off"
              />
            ))}
          </div>

          {error && <p className="text-sm text-error-600 text-center">{error}</p>}

          <div className="flex space-x-3 pt-4">
            <button type="button" onClick={onBack} className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg text-neutral-700 font-medium">
              Back
            </button>
            <button
              type="submit"
              disabled={loading || otp.join('').length !== 6}
              className="flex-1 px-4 py-3 bg-primary-600 text-neutral-50 rounded-lg font-semibold disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify →'}
            </button>
          </div>

          <div className="text-center">
            <button type="button" onClick={onResend} disabled={sending} className="text-sm text-primary-600 hover:text-primary-500 disabled:opacity-50">
              {sending ? 'Sending…' : 'Resend code'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// ─── Set PIN Screen ───────────────────────────────────────────────────────────

const SetPinScreen = ({ onSubmit, loading, error, onBack }) => {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localErr, setLocalErr] = useState(null);

  const onlyDigits = (setter) => (e) => setter(e.target.value.replace(/[^0-9]/g, '').slice(0, 4));

  const submit = (e) => {
    e.preventDefault();
    setLocalErr(null);
    if (!/^[0-9]{4}$/.test(pin)) { setLocalErr('PIN must be exactly 4 digits.'); return; }
    if (pin !== confirm) { setLocalErr('PINs do not match.'); return; }
    onSubmit(pin);
  };

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-neutral-100">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-neutral-900 mb-2">Set Your PIN</h1>
        <p className="text-neutral-600">Choose a 4-digit PIN. You'll use your phone + PIN to sign in.</p>
      </div>

      <form onSubmit={submit} className="flex-1 space-y-5">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Enter PIN</label>
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={pin}
            onChange={onlyDigits(setPin)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md text-center text-2xl tracking-[0.5em] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            autoComplete="off"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Confirm PIN</label>
          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={confirm}
            onChange={onlyDigits(setConfirm)}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md text-center text-2xl tracking-[0.5em] focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            autoComplete="off"
          />
        </div>

        {(localErr || error) && <p className="text-sm text-error-600 text-center">{localErr || error}</p>}

        <div className="flex space-x-3 pt-4">
          <button type="button" onClick={onBack} className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg text-neutral-700 font-medium">
            Back
          </button>
          <button
            type="submit"
            disabled={loading || pin.length !== 4 || confirm.length !== 4}
            className="flex-1 px-4 py-3 bg-primary-600 text-neutral-50 rounded-lg font-semibold disabled:opacity-50"
          >
            {loading ? 'Setting…' : 'Set PIN →'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Success Screen ───────────────────────────────────────────────────────────

const InviteSuccessScreen = ({ memberName, groupName, navigate }) => (
  <div className="flex flex-col items-center justify-center min-h-screen px-4 py-8 bg-gradient-to-b from-neutral-100 to-neutral-50 text-center">
    <div className="w-20 h-20 bg-success-600 rounded-full flex items-center justify-center mb-6">
      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <h1 className="text-3xl font-bold text-neutral-900 mb-2">You're in!</h1>
    <p className="text-lg text-neutral-600 mb-8">Welcome to {groupName}</p>

    <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-5 w-full max-w-sm text-left space-y-2 mb-8">
      <p className="text-sm text-neutral-700"><strong>Name:</strong> {memberName}</p>
      <p className="text-sm text-neutral-700"><strong>Group:</strong> {groupName}</p>
      <p className="text-sm text-neutral-600 mt-3">
        Your hands and rotation positions have already been assigned by your admin.
        You can view your schedule on your dashboard.
      </p>
    </div>

    <button
      onClick={() => navigate('/member/dashboard')}
      className="w-full max-w-sm bg-primary-600 text-neutral-50 rounded-lg px-4 py-4 text-base font-semibold"
    >
      Go to My Dashboard →
    </button>
  </div>
);

// ─── Main Wizard ──────────────────────────────────────────────────────────────

const STEPS = { LANDING: 1, AGREEMENT: 2, VERIFY_PHONE: 3, SET_PIN: 4, SUCCESS: 5 };

const InviteOnboardingWizard = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.LANDING);
  const [invite, setInvite] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [codeSent, setCodeSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [devCode, setDevCode] = useState(null);

  useEffect(() => {
    const loadInvite = async () => {
      try {
        if (!token) throw new Error('Invalid invite link');
        const data = await getInviteByToken(token);

        if (data.status === 'accepted') {
          setError('This invite has already been accepted. Please sign in to access your dashboard.');
          return;
        }
        if (data.status === 'expired' || new Date(data.expires_at) < new Date()) {
          setError('This invite link has expired. Please contact your group admin for a new invite.');
          return;
        }
        setInvite(data);
      } catch (err) {
        console.error('Failed to load invite:', err);
        setError('Invalid or expired invite link. Please contact your group admin.');
      } finally {
        setLoading(false);
      }
    };
    loadInvite();
  }, [token]);

  // Agreement → PERSIST CONSENT, then phone verification. The consent record is
  // the whole point: signature + agreed + server-stamped time + terms version &
  // hash are written via the token-authorized record_consent RPC (the member is
  // still anonymous here — the invite token is the authorization). We BLOCK the
  // advance if persistence fails, so we never move a member past a consent we
  // didn't actually record.
  const handleAgreementNext = async (data) => {
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const { error: consentErr } = await supabaseClient.rpc('record_consent', {
        p_invite_token: token,
        p_signature: data.signature,
        p_agreed: data.agreed,
        p_terms_version: data.termsVersion,
        p_terms_sha256: data.termsSha256,
        p_client_signed_at: data.signedAt,
        p_first_name: data.firstName,
        p_last_name: data.lastName,
        p_privacy_accepted: data.privacyAccepted,
        p_privacy_version: data.privacyVersion,
        p_tc_accepted: data.tcAccepted,
        p_tc_version: data.tcVersion,
        p_messaging_accepted: data.messagingAccepted,
        p_messaging_version: data.messagingVersion,
        p_messaging_method: data.messagingMethod,
        p_display_alias: data.displayAlias,   // optional onboarding display name (Stage 47)
      });
      if (consentErr) throw new Error(consentErr.message || 'Could not record your agreement.');

      setAgreementData(data);
      setStep(STEPS.VERIFY_PHONE);
    } catch (err) {
      // Surface a clear server reason (e.g. a per-group name collision) so the
      // member knows what to fix; fall back to a generic message otherwise.
      setSubmitError(
        err?.message
          ? err.message
          : 'We could not record your agreement, so we can\'t continue yet. Please try again, or contact your group admin.'
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // Ask the server to mint + send the OTP. Generation/delivery is fully
  // server-side now (send-otp Edge Function, authorized by the invite token);
  // the client no longer calls request_otp and never sees the code in prod. A
  // code comes back ONLY when the server dev-echo flag is on (staging) — then we
  // show the dev banner.
  const handleSendOtp = async () => {
    setSendingOtp(true);
    setSubmitError(null);
    try {
      const { code } = await sendOtp({ inviteToken: token });
      if (code) setDevCode(code);
      setCodeSent(true);
    } catch (err) {
      setSubmitError(err.message || 'Could not send code.');
    } finally {
      setSendingOtp(false);
    }
  };

  // Verify the entered OTP (verify_otp RPC).
  const handleVerifyOtp = async (code) => {
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const memberId = invite?.members?.id;
      const { data, error: vErr } = await supabaseClient.rpc('verify_otp', {
        p_member_id: memberId,
        p_code: code,
        p_purpose: 'first_join',
      });
      if (vErr) throw new Error(vErr.message || 'Verification failed.');
      if (!data || data.success !== true) {
        const messages = {
          expired: 'That code has expired. Please request a new one.',
          too_many_attempts: 'Too many attempts. Please request a new code.',
          wrong_code: 'Incorrect code. Please try again.',
          no_code: 'Please request a code first.',
        };
        throw new Error(messages[data && data.reason] || 'Verification failed.');
      }
      setDevCode(null);
      setStep(STEPS.SET_PIN);
    } catch (err) {
      setSubmitError(err.message || 'Verification failed.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Set the member's PIN (set_pin RPC, authorized by the invite token), store the
  // returned permanent login token, then mint a real session via the pin-login
  // bridge so the dashboard (ProtectedRoute) works.
  const handleSetPin = async (pin) => {
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const { data: loginToken, error: setErr } = await supabaseClient.rpc('set_pin', {
        p_invite_token: token,
        p_pin: pin,
      });
      if (setErr) throw new Error(setErr.message || 'Could not set your PIN.');
      if (!loginToken) throw new Error('Could not set your PIN. Please try again.');

      // Persist the member's permanent "door" (per Section 6).
      localStorage.setItem('memberLoginToken', loginToken);

      // Establish a real Supabase session from the login token + PIN.
      const result = await loginWithPin(loginToken, pin);
      if (result.success) {
        // The optional display name is saved earlier, at the agreement step, via
        // record_consent (Stage 47) — the invite flow does not mint a session here.
        localStorage.setItem('userRole', 'member');
        navigate('/member/dashboard');
        return;
      }

      // PIN is set, but the session could not be minted (e.g. pin-login not reachable
      // or member.phone_e164 missing). Surface it; land on the success screen.
      setSubmitError(
        'Your PIN was set, but automatic sign-in is unavailable right now. ' +
        'You can sign in later with your phone and PIN.'
      );
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setSubmitError(err.message || 'Could not set your PIN.');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-error-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-900 mb-2">Invite Not Available</h2>
          <p className="text-neutral-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!invite) return null;

  const { members: member, groups: group } = invite;
  const agreementData_ = {
    name: member?.name,
    groupName: group?.group_name,
    amount: group?.contribution_amount_per_hand,
    frequency: group?.contribution_frequency,
  };

  const rawPhone = member?.whatsapp_number || member?.sms_number || '';
  const phoneMasked = rawPhone ? `•••• ${rawPhone.replace(/\D/g, '').slice(-4)}` : '';

  switch (step) {
    case STEPS.LANDING:
      return (
        <InviteLandingPage
          invite={invite}
          onAccept={() => setStep(STEPS.AGREEMENT)}
        />
      );

    case STEPS.AGREEMENT:
      return (
        <>
          <ContributionAgreementScreen
            data={agreementData_}
            onNext={handleAgreementNext}
            onBack={() => setStep(STEPS.LANDING)}
            submitting={submitLoading}
          />
          {submitError && (
            <div className="fixed bottom-4 left-4 right-4 max-w-sm mx-auto bg-error-50 border border-error-200 rounded-lg p-3">
              <p className="text-sm text-error-800">{submitError}</p>
            </div>
          )}
        </>
      );

    case STEPS.VERIFY_PHONE:
      return (
        <>
          {/* Shown only when the server echoed a code (staging dev flag); the
              server never echoes in production, so this stays hidden there. */}
          {devCode && (
            <DevOtpBanner code={devCode} onDismiss={() => setDevCode(null)} />
          )}
          <PhoneVerifyScreen
            phoneMasked={phoneMasked}
            codeSent={codeSent}
            onSend={handleSendOtp}
            onResend={handleSendOtp}
            onSubmit={handleVerifyOtp}
            sending={sendingOtp}
            loading={submitLoading}
            error={submitError}
            onBack={() => {
              setCodeSent(false);
              setDevCode(null);
              setSubmitError(null);
              setStep(STEPS.AGREEMENT);
            }}
          />
        </>
      );

    case STEPS.SET_PIN:
      return (
        <SetPinScreen
          onSubmit={handleSetPin}
          loading={submitLoading}
          error={submitError}
          onBack={() => {
            setSubmitError(null);
            setStep(STEPS.VERIFY_PHONE);
          }}
        />
      );

    case STEPS.SUCCESS:
      return (
        <InviteSuccessScreen
          memberName={member?.name}
          groupName={group?.group_name}
          navigate={navigate}
        />
      );

    default:
      return null;
  }
};

export default InviteOnboardingWizard;
